import { ProbeRun, Monitor, MonitorGroup, MonitorRun, MonitorIncident, StatusPage, PublicStatusPage, MonitorCacheStats } from "../types";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/v1";

let getTokenFn: (() => Promise<string | null>) | null = null;

export function setGetTokenFn(fn: (() => Promise<string | null>) | null): void {
  getTokenFn = fn;
}

async function getToken(): Promise<string | null> {
  return getTokenFn ? getTokenFn() : null;
}

async function request<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "Unknown error" }));
    const err = new ApiError(response.status, body.error ?? "Request failed", body);
    throw err;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isGated(): boolean {
    return this.body?.gated === true;
  }

  get isUpgradeRequired(): boolean {
    return this.body?.code === "upgrade_required";
  }
}

// ── Current user ─────────────────────────────────────────────────────────────

export interface MeResponse {
  plan: string;
  email: string;
}

export async function getMe(): Promise<MeResponse> {
  return request("/me");
}

// ── Probes ───────────────────────────────────────────────────────────────────

export interface CreateProbeParams {
  url: string;
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  regions?: string[];
}

export async function createProbe(params: CreateProbeParams): Promise<{ run_id: string }> {
  return request("/probe", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function getProbe(runId: string): Promise<ProbeRun> {
  return request(`/probe/${runId}`);
}

export async function getShareResult(runId: string): Promise<ProbeRun> {
  return request(`/probe/${runId}/share`);
}

export async function getProbeHistory(limit = 20, offset = 0): Promise<{ runs: ProbeRun[] }> {
  return request(`/probe?limit=${limit}&offset=${offset}`);
}

// ── Monitors ─────────────────────────────────────────────────────────────────

export interface CreateMonitorParams {
  name: string;
  url: string;
  method: string;
  headers?: Record<string, string>;
  body?: string;
  interval_minutes: number;
  regions: string[];
  alert_latency_ms?: number;
  alert_cert_expiry_days?: number;
  alert_email?: string;
  alert_webhook_url?: string;
  group_id?: string | null;
  // TCP-specific options; only sent when method == "TCP".
  tcp_tls?: boolean;
  tcp_skip_verify?: boolean;
  tcp_send_string?: string | null;
  tcp_expect_string?: string | null;
  tcp_timeout_secs?: number;
}

export async function listMonitors(): Promise<{ monitors: Monitor[] }> {
  return request("/monitors");
}

export async function createMonitor(params: CreateMonitorParams): Promise<Monitor> {
  return request("/monitors", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function updateMonitor(id: string, params: Partial<CreateMonitorParams>): Promise<Monitor> {
  return request(`/monitors/${id}`, {
    method: "PATCH",
    body: JSON.stringify(params),
  });
}

export async function deleteMonitor(id: string): Promise<void> {
  return request(`/monitors/${id}`, { method: "DELETE" });
}

export async function listMonitorRuns(
  monitorId: string,
  limit = 50,
  offset = 0
): Promise<{ runs: MonitorRun[] }> {
  return request(`/monitors/${monitorId}/runs?limit=${limit}&offset=${offset}`);
}

export interface UptimeDay {
  date: string; // "YYYY-MM-DD" in the requested timezone
  status: "up" | "down" | "degraded" | "no-data";
  up_count: number;
  down_count: number;
}

export interface LatencyBucket {
  time: string;
  p50: number | null;
  p95: number | null;
  p99: number | null;
  error_count: number;
  total_count: number;
}

export async function getMonitorLatency(
  monitorId: string,
  range: "1h" | "24h" | "7d" | "30d" | "custom",
  customSince?: string,
  customUntil?: string,
): Promise<{ buckets: LatencyBucket[]; earliest_data_at: string | null }> {
  let url = `/monitors/${monitorId}/latency?range=${range}`;
  if (range === "custom" && customSince && customUntil) {
    url += `&since=${encodeURIComponent(customSince)}&until=${encodeURIComponent(customUntil)}`;
  }
  return request(url);
}

export interface MonitorIncidentsResponse {
  incidents: MonitorIncident[];
  has_more: boolean;
  threshold_ms: number;
}

export async function getMonitorIncidents(
  monitorId: string,
  params: {
    type?: "error" | "latency" | "all";
    since?: string;
    until?: string;
    threshold_ms?: number;
    limit?: number;
    offset?: number;
  } = {}
): Promise<MonitorIncidentsResponse> {
  const q = new URLSearchParams();
  if (params.type) q.set("type", params.type);
  if (params.since) q.set("since", params.since);
  if (params.until) q.set("until", params.until);
  if (params.threshold_ms != null) q.set("threshold_ms", String(params.threshold_ms));
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return request(`/monitors/${monitorId}/incidents?${q.toString()}`);
}

export async function getMonitorUptime(
  monitorId: string,
  tz: string
): Promise<{ days: UptimeDay[]; uptime_pct: number }> {
  return request(`/monitors/${monitorId}/uptime?tz=${encodeURIComponent(tz)}`);
}

export async function getMonitorCache(
  monitorId: string,
  range: "1h" | "24h" | "7d" | "30d"
): Promise<MonitorCacheStats> {
  return request(`/monitors/${monitorId}/cache?range=${range}`);
}

export interface UpdateMonitorAlertsParams {
  alert_latency_ms?: number | null;
  alert_cert_expiry_days?: number | null;
  alert_email?: string | null;
  alert_webhook_url?: string | null;
}

export async function updateMonitorAlerts(id: string, params: UpdateMonitorAlertsParams): Promise<Monitor> {
  return request(`/monitors/${id}/alerts`, {
    method: "PATCH",
    body: JSON.stringify(params),
  });
}

export async function setMonitorGroup(monitorId: string, groupId: string | null): Promise<void> {
  return request(`/monitors/${monitorId}/group`, {
    method: "PATCH",
    body: JSON.stringify({ group_id: groupId }),
  });
}

// ── Monitor Groups ────────────────────────────────────────────────────────────

export interface CreateMonitorGroupParams {
  name: string;
  description?: string | null;
  color: string;
}

export async function listMonitorGroups(): Promise<{ groups: MonitorGroup[] }> {
  return request("/monitor-groups");
}

export async function createMonitorGroup(params: CreateMonitorGroupParams): Promise<MonitorGroup> {
  return request("/monitor-groups", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function getMonitorGroup(id: string): Promise<MonitorGroup> {
  return request(`/monitor-groups/${id}`);
}

export async function updateMonitorGroup(id: string, params: CreateMonitorGroupParams): Promise<MonitorGroup> {
  return request(`/monitor-groups/${id}`, {
    method: "PATCH",
    body: JSON.stringify(params),
  });
}

export async function deleteMonitorGroup(id: string): Promise<void> {
  return request(`/monitor-groups/${id}`, { method: "DELETE" });
}

// ── Status Pages ─────────────────────────────────────────────────────────────

export interface StatusPageMonitorInput {
  monitor_id: string;
  display_name?: string | null;
  display_order: number;
}

export interface StatusPageParams {
  title: string;
  slug: string;
  description?: string | null;
  is_public: boolean;
  monitors: StatusPageMonitorInput[];
}

export async function listStatusPages(): Promise<{ pages: StatusPage[] }> {
  return request("/status-pages");
}

export async function createStatusPage(params: StatusPageParams): Promise<StatusPage> {
  return request("/status-pages", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function updateStatusPage(id: string, params: StatusPageParams): Promise<StatusPage> {
  return request(`/status-pages/${id}`, {
    method: "PATCH",
    body: JSON.stringify(params),
  });
}

export async function deleteStatusPage(id: string): Promise<void> {
  return request(`/status-pages/${id}`, { method: "DELETE" });
}

export async function getPublicStatusPage(slug: string): Promise<PublicStatusPage> {
  return request(`/status-pages/${slug}`);
}

// ── Billing ──────────────────────────────────────────────────────────────────

export async function createCheckout(plan: "monthly" | "annual"): Promise<{ url: string }> {
  return request("/billing/checkout", {
    method: "POST",
    body: JSON.stringify({ plan }),
  });
}

export async function createPortal(): Promise<{ url: string }> {
  return request("/billing/portal", { method: "POST" });
}
